import { useState, useEffect, useRef, useMemo } from "react";
import { Container } from "react-bootstrap";

/* ============================================================
   CAREER TIMELINE  —  Clean Modern Design
   ============================================================ */

export const CareerTimeline = () => {
  const [visibleItems, setVisibleItems] = useState(new Set());
  const [lineProgress, setLineProgress] = useState(0);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [counters, setCounters] = useState({ exp: 0, pos: 0, proj: 0 });

  const sectionRef = useRef(null);
  const headerRef  = useRef(null);

  const timelineData = useMemo(() => [
    {
      id: 1,
      date: "2025 July – Present",
      title: "Associate Software Engineer",
      company: "Fonix Edu Pvt Ltd · Atlas Axilla Group",
      description:
        "Building key frontend features for a digital education platform with React.js. Integrating REST APIs and deploying OpenAI models for intelligent content processing. Fine-tuning Hugging Face models in Python for accurate handwritten text detection.",
      icon: "💼",
      type: "work",
      color: "#06b6d4",
      skills: ["React.js", "OpenAI", "Python", "REST API"],
    },
    {
      id: 2,
      date: "2025 Feb – 2025 July",
      title: "Associate Software Engineer",
      company: "Fonix Software Solution",
      description:
        "Delivered admin panels and dashboards for Coach Donee, Korean Master, and Rotary in React.js and TypeScript. Engineered an LMS backend with Python, OpenAI, Hugging Face, and Groq AI. Built a Grading System combining Google OCR, TestReacted OCR, Claude AI, and OpenAI.",
      icon: "🚀",
      type: "work",
      color: "#8b5cf6",
      skills: ["TypeScript", "Python", "AI / ML", "OCR"],
    },
    {
      id: 3,
      date: "2024 Feb – 2025 Feb",
      title: "Software Engineer Intern",
      company: "Fonix Software Solution",
      description:
        "Created frontend landing pages and admin/client UIs in React.js. Built backend APIs and mobile routes with Node.js and Express.js. Implemented auth, route protection, PayHere & Direct Pay gateways, and WebSocket-driven real-time multiplayer in ExamHub.",
      icon: "💻",
      type: "work",
      color: "#ec4899",
      skills: ["Node.js", "Express.js", "WebSocket", "Payments"],
    },
    {
      id: 4,
      date: "2022 – Present",
      title: "BSc (Hons) in Information Technology",
      company: "Sri Lanka Institute of Information Technology",
      description:
        "Specialising in Information Technology with focus on software engineering, web development, and modern programming paradigms.",
      icon: "🎓",
      type: "education",
      color: "#10b981",
      skills: ["Software Engineering", "Web Dev", "Programming"],
    },
    {
      id: 5,
      date: "2018 – 2020",
      title: "G.C.E Advanced Level",
      company: "St Aloysius College Rathnapura",
      description:
        "Completed G.C.E Advanced Level 2020, Physical Science stream (C3). Built a strong foundation in mathematics and scientific principles.",
      icon: "📚",
      type: "education",
      color: "#f59e0b",
      skills: ["Physics", "Mathematics", "Chemistry"],
    },
  ], []);

  /* ── HEADER + COUNTER ── */
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setHeaderVisible(true);
          const targets = { exp: 2, pos: 5, proj: 10 };
          const dur = 1800;
          const start = performance.now();
          const tick = (now) => {
            const t    = Math.min((now - start) / dur, 1);
            const ease = 1 - Math.pow(1 - t, 3);
            setCounters({
              exp:  Math.round(targets.exp  * ease),
              pos:  Math.round(targets.pos  * ease),
              proj: Math.round(targets.proj * ease),
            });
            if (t < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 }
    );
    if (headerRef.current) obs.observe(headerRef.current);
    return () => obs.disconnect();
  }, []);

  /* ── ITEM REVEAL ── */
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const id = parseInt(e.target.dataset.itemId);
            setVisibleItems((prev) => new Set([...prev, id]));
          }
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll(".ctl-item").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  /* ── SCROLL LINE PROGRESS ── */
  useEffect(() => {
    const onScroll = () => {
      if (!sectionRef.current) return;
      const r = sectionRef.current.getBoundingClientRect();
      const p = Math.max(0, Math.min(1, (-r.top + window.innerHeight * 0.5) / r.height));
      setLineProgress(p);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ─── RENDER ─── */
  return (
    <section className="ctl-section" id="timeline" ref={sectionRef}>
      {/* decorative dot-grid */}
      <div className="ctl-grid-bg" aria-hidden="true" />

      <Container className="ctl-container-wrap">

        {/* ━━━ HEADER ━━━ */}
        <div
          className={`ctl-header ${headerVisible ? "ctl-header--visible" : ""}`}
          ref={headerRef}
        >
          <div className="ctl-eyebrow">
            <span className="ctl-eyebrow-dot" />
            Experience &amp; Education
          </div>

          <h2 className="ctl-heading">
            My{" "}
            <span className="ctl-gradient-text">Career</span>{" "}
            Journey
          </h2>

          <p className="ctl-subheading">
            A curated timeline of professional milestones, technical growth, and academic foundations.
          </p>

          {/* Stats row */}
          <div className="ctl-stats">
            {[
              { value: counters.exp,  suffix: "+", label: "Years Experience", icon: "⚡" },
              { value: counters.pos,  suffix: "+", label: "Positions Held",   icon: "🎯" },
              { value: counters.proj, suffix: "+", label: "Projects Shipped", icon: "🚀" },
            ].map((s, i) => (
              <div
                className="ctl-stat-card"
                key={i}
                style={{ "--delay": `${i * 120 + 400}ms` }}
              >
                <span className="ctl-stat-icon">{s.icon}</span>
                <span className="ctl-stat-num">{s.value}{s.suffix}</span>
                <span className="ctl-stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ━━━ TIMELINE ━━━ */}
        <div className="ctl-track">

          {/* Scroll-driven line */}
          <div className="ctl-line-bg" />
          <div className="ctl-line-fill" style={{ height: `${lineProgress * 100}%` }} />
          {/* Glowing tracker orb */}
          <div className="ctl-orb" style={{ top: `${lineProgress * 100}%` }} />

          {timelineData.map((item, index) => {
            const isLeft    = index % 2 === 0;
            const isVisible = visibleItems.has(item.id);
            const isPresent = item.date.includes("Present");

            return (
              <div
                key={item.id}
                className={`ctl-item ${isLeft ? "ctl-item--left" : "ctl-item--right"} ${isVisible ? "ctl-item--visible" : ""}`}
                data-item-id={item.id}
                style={{ "--item-color": item.color, "--delay": `${index * 100}ms` }}
              >
                {/* ── Card ── */}
                <div className="ctl-card">
                  {/* colored top accent */}
                  <div className="ctl-card-accent" style={{ background: item.color }} />

                  {/* header row */}
                  <div className="ctl-card-head">
                    <div
                      className="ctl-date-badge"
                      style={{
                        background: `${item.color}14`,
                        color:       item.color,
                        borderColor: `${item.color}30`,
                      }}
                    >
                      {isPresent && (
                        <span className="ctl-live" style={{ background: item.color }} />
                      )}
                      {item.date}
                    </div>

                    <div
                      className="ctl-type-pill"
                      style={{
                        background:  `${item.color}10`,
                        color:        item.color,
                        borderColor: `${item.color}25`,
                      }}
                    >
                      {item.type === "work" ? "Work" : "Education"}
                    </div>
                  </div>

                  {/* title + company */}
                  <h3 className="ctl-title">{item.title}</h3>
                  <p className="ctl-company">
                    <span
                      className="ctl-company-icon"
                      style={{ background: `${item.color}15`, color: item.color }}
                    >
                      {item.type === "work" ? "🏢" : "🎓"}
                    </span>
                    {item.company}
                  </p>

                  {/* description */}
                  <p className="ctl-desc">{item.description}</p>

                  {/* skills */}
                  <div className="ctl-skills">
                    {item.skills.map((skill, si) => (
                      <span
                        key={si}
                        className="ctl-chip"
                        style={{
                          "--chip-color": item.color,
                          animationDelay: isVisible ? `${si * 80 + 300}ms` : "0ms",
                        }}
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* ── Center Node ── */}
                <div className="ctl-node-wrap">
                  <div
                    className="ctl-node"
                    style={{
                      background: `linear-gradient(135deg, ${item.color}cc, ${item.color}66)`,
                      boxShadow:  `0 0 0 3px #0a0a0f, 0 0 0 5px ${item.color}35, 0 8px 24px ${item.color}40`,
                    }}
                  >
                    <span className="ctl-node-emoji">{item.icon}</span>
                  </div>
                  <div className="ctl-node-index" style={{ color: `${item.color}50` }}>
                    {String(index + 1).padStart(2, "0")}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Container>

      {/* ━━━ SCOPED STYLES ━━━ */}
      <style>{`

/* ── SECTION ── */
.ctl-section {
  position: relative;
  padding: 100px 0 80px;
  background: var(--bg-secondary, #0a0a0f);
  overflow: hidden;
}

/* subtle dot-grid background */
.ctl-grid-bg {
  position: absolute; inset: 0;
  background-image:
    radial-gradient(circle at 15% 20%, rgba(6,182,212,0.06) 0%, transparent 45%),
    radial-gradient(circle at 85% 75%, rgba(139,92,246,0.06) 0%, transparent 45%),
    radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px);
  background-size: auto, auto, 32px 32px;
  pointer-events: none;
  z-index: 0;
}

.ctl-container-wrap { position: relative; z-index: 1; }

/* ── HEADER ── */
.ctl-header {
  text-align: center;
  margin-bottom: 88px;
}
.ctl-header > * {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.7s ease, transform 0.7s ease;
}
.ctl-header--visible > *:nth-child(1) { opacity:1; transform:none; transition-delay:  0ms; }
.ctl-header--visible > *:nth-child(2) { opacity:1; transform:none; transition-delay:100ms; }
.ctl-header--visible > *:nth-child(3) { opacity:1; transform:none; transition-delay:200ms; }
.ctl-header--visible > *:nth-child(4) { opacity:1; transform:none; transition-delay:350ms; }

/* eyebrow badge */
.ctl-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 12px; font-weight: 700; letter-spacing: 1.4px;
  text-transform: uppercase; color: #06b6d4;
  background: rgba(6,182,212,0.07);
  border: 1px solid rgba(6,182,212,0.14);
  border-radius: 50px; padding: 7px 18px;
  margin-bottom: 24px;
}
.ctl-eyebrow-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: #06b6d4;
  box-shadow: 0 0 8px #06b6d4;
  animation: ctlPulse 2s ease-in-out infinite;
}
@keyframes ctlPulse {
  0%,100% { transform: scale(1); opacity:1; }
  50%      { transform: scale(1.5); opacity:0.5; }
}

/* heading */
.ctl-heading {
  font-size: clamp(34px, 5vw, 54px);
  font-weight: 800; color: #f0f0f0;
  letter-spacing: -1.5px; line-height: 1.15;
  margin-bottom: 16px;
}
.ctl-gradient-text {
  background: linear-gradient(135deg, #06b6d4 0%, #8b5cf6 50%, #ec4899 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.ctl-subheading {
  font-size: 16px; color: #a0a0a8;
  max-width: 500px; margin: 0 auto 40px;
  line-height: 1.75;
}

/* stats */
.ctl-stats {
  display: flex; justify-content: center;
  gap: 20px; flex-wrap: wrap;
}
.ctl-stat-card {
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  background: rgba(255,255,255,0.025);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 16px; padding: 20px 28px;
  min-width: 130px;
  opacity: 0; transform: translateY(16px);
  transition: opacity 0.6s ease, transform 0.6s ease, background 0.3s ease, border-color 0.3s ease;
}
.ctl-header--visible .ctl-stat-card {
  opacity: 1; transform: none;
  transition-delay: var(--delay, 0ms);
}
.ctl-stat-card:hover {
  background: rgba(255,255,255,0.05);
  border-color: rgba(255,255,255,0.12);
  transform: translateY(-4px);
}
.ctl-stat-icon { font-size: 22px; margin-bottom: 4px; }
.ctl-stat-num {
  font-size: 34px; font-weight: 900;
  background: linear-gradient(135deg, #06b6d4, #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}
.ctl-stat-label {
  font-size: 11px; font-weight: 600;
  color: #6b6b75; text-transform: uppercase;
  letter-spacing: 1.2px; margin-top: 2px;
}

/* ── TRACK ── */
.ctl-track {
  position: relative;
  max-width: 1080px;
  margin: 0 auto;
  padding: 20px 0 40px;
}

/* timeline spine */
.ctl-line-bg,
.ctl-line-fill {
  position: absolute;
  left: 50%; top: 0;
  width: 2px;
  transform: translateX(-50%);
}
.ctl-line-bg {
  bottom: 0;
  background: rgba(255,255,255,0.04);
  border-radius: 2px;
}
.ctl-line-fill {
  background: linear-gradient(180deg, #06b6d4, #8b5cf6, #ec4899);
  border-radius: 2px;
  transition: height 0.06s linear;
  box-shadow: 0 0 10px rgba(6,182,212,0.25);
}

/* glowing orb tracker */
.ctl-orb {
  position: absolute;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 12px; height: 12px;
  background: #06b6d4;
  border-radius: 50%;
  box-shadow:
    0 0 0 4px #0a0a0f,
    0 0 0 6px rgba(6,182,212,0.25),
    0 0 18px rgba(6,182,212,0.5);
  animation: ctlOrb 2s ease-in-out infinite;
  transition: top 0.06s linear;
  z-index: 4;
}
@keyframes ctlOrb {
  0%,100% { transform: translate(-50%,-50%) scale(1); }
  50%      { transform: translate(-50%,-50%) scale(1.3); }
}

/* ── ITEMS ── */
.ctl-item {
  position: relative;
  margin-bottom: 72px;
  display: flex;
  align-items: center;
  opacity: 0;
  transition: opacity 0.8s ease, transform 0.8s cubic-bezier(0.16,1,0.3,1);
}
.ctl-item--left {
  flex-direction: row;
  padding-right: calc(50% + 60px);
  transform: translateX(-60px);
}
.ctl-item--right {
  flex-direction: row-reverse;
  padding-left: calc(50% + 60px);
  transform: translateX(60px);
}
.ctl-item--visible {
  opacity: 1;
  transform: translateX(0);
  transition-delay: var(--delay, 0ms);
}

/* ── CARD ── */
.ctl-card {
  position: relative;
  width: 100%;
  max-width: 460px;
  background: rgba(255,255,255,0.025);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 18px;
  padding: 28px 28px 24px;
  overflow: hidden;
  transition: background 0.35s ease, border-color 0.35s ease, transform 0.35s ease, box-shadow 0.35s ease;
}
.ctl-card:hover {
  background: rgba(255,255,255,0.05);
  border-color: color-mix(in srgb, var(--item-color, #06b6d4) 30%, transparent);
  transform: translateY(-6px);
  box-shadow: 0 24px 56px rgba(0,0,0,0.3);
}

/* top color bar */
.ctl-card-accent {
  position: absolute; top: 0; left: 0; right: 0;
  height: 3px; opacity: 0.55;
  border-radius: 18px 18px 0 0;
  transition: opacity 0.35s ease;
}
.ctl-card:hover .ctl-card-accent { opacity: 1; }

/* header row */
.ctl-card-head {
  display: flex; align-items: center;
  justify-content: space-between;
  flex-wrap: wrap; gap: 8px;
  margin-bottom: 16px;
}

/* date badge */
.ctl-date-badge {
  display: inline-flex; align-items: center; gap: 7px;
  font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 1.3px;
  padding: 5px 13px; border-radius: 50px;
  border: 1px solid;
}
.ctl-live {
  width: 6px; height: 6px; border-radius: 50%;
  flex-shrink: 0;
  animation: ctlLive 1.6s ease-in-out infinite;
}
@keyframes ctlLive {
  0%,100% { opacity:1; transform:scale(1); }
  50%      { opacity:0.4; transform:scale(1.6); }
}

/* type pill */
.ctl-type-pill {
  font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 1px;
  padding: 4px 12px; border-radius: 50px;
  border: 1px solid;
}

/* title */
.ctl-title {
  font-size: 20px; font-weight: 700;
  color: #f0f0f0; margin-bottom: 7px;
  line-height: 1.3;
  transition: color 0.25s ease;
}
.ctl-card:hover .ctl-title { color: #fff; }

/* company */
.ctl-company {
  display: flex; align-items: center; gap: 8px;
  font-size: 13px; color: #a0a0a8; font-weight: 500;
  margin-bottom: 14px;
}
.ctl-company-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 6px;
  font-size: 11px; flex-shrink: 0;
}

/* description */
.ctl-desc {
  font-size: 13.5px; color: #8a8a96;
  line-height: 1.75; margin-bottom: 18px;
}

/* skills */
.ctl-skills { display: flex; flex-wrap: wrap; gap: 7px; }
.ctl-chip {
  font-size: 11px; font-weight: 600;
  padding: 4px 12px; border-radius: 50px;
  border: 1px solid var(--chip-color, #06b6d4);
  color: var(--chip-color, #06b6d4);
  background: rgba(255,255,255,0.02);
  letter-spacing: 0.3px;
  opacity: 0; transform: translateY(8px);
  transition: background 0.25s ease, transform 0.25s ease;
}
.ctl-item--visible .ctl-chip {
  animation: ctlChip 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards;
  animation-delay: var(--delay, 0ms);
}
@keyframes ctlChip { to { opacity:1; transform:none; } }
.ctl-chip:hover {
  background: rgba(255,255,255,0.06);
  transform: translateY(-2px) !important;
}

/* ── NODE ── */
.ctl-node-wrap {
  position: absolute;
  left: 50%; top: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
  display: flex; flex-direction: column;
  align-items: center; gap: 6px;
}
.ctl-node {
  width: 52px; height: 52px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  transition: transform 0.4s cubic-bezier(0.16,1,0.3,1), box-shadow 0.4s ease;
}
.ctl-item:hover .ctl-node { transform: scale(1.2) rotate(12deg); }
.ctl-node-emoji {
  font-size: 24px;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));
  transition: transform 0.4s ease;
}
.ctl-item:hover .ctl-node-emoji { transform: scale(1.15); }
.ctl-node-index {
  font-size: 10px; font-weight: 800;
  letter-spacing: 1px;
}

/* ── RESPONSIVE ── */
@media (max-width: 991px) {
  .ctl-line-bg, .ctl-line-fill { left: 26px; }
  .ctl-orb { left: 26px; }
  .ctl-item--left,
  .ctl-item--right {
    flex-direction: row;
    padding-left: 74px;
    padding-right: 0;
    transform: translateY(30px);
  }
  .ctl-item--visible { transform: none !important; }
  .ctl-node-wrap { left: 26px; }
  .ctl-card { max-width: 100%; }
}
@media (max-width: 768px) {
  .ctl-section { padding: 80px 0 60px; }
  .ctl-header  { margin-bottom: 64px; }
  .ctl-item    { margin-bottom: 52px; }
  .ctl-card    { padding: 22px 20px 18px; }
  .ctl-title   { font-size: 18px; }
  .ctl-stat-card { min-width: 110px; padding: 16px 20px; }
  .ctl-stat-num  { font-size: 28px; }
}
@media (max-width: 575px) {
  .ctl-line-bg, .ctl-line-fill { left: 18px; }
  .ctl-orb { left: 18px; }
  .ctl-item--left,
  .ctl-item--right { padding-left: 58px; }
  .ctl-node-wrap { left: 18px; }
  .ctl-node { width: 42px; height: 42px; }
  .ctl-node-emoji { font-size: 20px; }
  .ctl-heading { letter-spacing: -1px; }
  .ctl-stats   { gap: 12px; }
  .ctl-stat-card { padding: 14px 16px; min-width: 95px; }
}

      `}</style>
    </section>
  );
};
